import { Button } from '@/components/ui/button';
import { CreateOrganizationModal } from './create-organization-modal.tsx';
import { useEffect, useState, useMemo } from 'react';
import { OrganizationItem } from './organization-item.tsx';
import { AddMemberModal } from '@/components/add-member-modal';
import { Organization } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetOrganizations,
  ORGANIZATIONS_QUERY_KEY,
} from '@/hooks/queries/admin/use-get-organizations';
import { useSetOrgArchived } from '@/hooks/mutations/admin/use-set-org-archived';
import { AdminSkeleton } from './admin-skeleton.tsx';
import { useNotification } from '@/components/notifications';
import { PageTitle } from '@/components/page-title';
import { SearchInput } from '@/components/ui/search-input';
import { Pagination } from '@/components/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  filterOrganizations,
  OrgArchiveFilter,
} from './helpers/filter-organizations.ts';
import * as Sentry from '@sentry/react';

const ITEMS_PER_PAGE = 20;

export const AdminPage = () => {
  const [isCreateOrgModalOpen, setCreateOrgModalOpen] = useState(false);
  const [isAddMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [archiveFilter, setArchiveFilter] =
    useState<OrgArchiveFilter>('active');
  const [currentPage, setCurrentPage] = useState(1);
  const notification = useNotification();
  const queryClient = useQueryClient();

  const {
    data: organizationList = [],
    isLoading,
    isError,
    error,
  } = useGetOrganizations();
  const { mutate: setArchived, isPending: isArchiving } = useSetOrgArchived();

  useEffect(() => {
    if (!isError) return;
    Sentry.captureException(error, {
      extra: { context: 'AdminPage.useGetOrganizations' },
    });
    notification.error(
      error instanceof Error ? error.message : 'An unexpected error occurred',
    );
  }, [isError, error, notification]);

  const handleOpenMemberModal = (id: string) => {
    setAddMemberModalOpen(true);
    setOrgId(id);
  };

  const refetchOrganizations = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
  };

  const handleToggleArchive = (org: Organization) => {
    if (isArchiving) return;
    const nextArchived = !org.archived;
    setArchived(
      { orgId: org.id, archived: nextArchived },
      {
        onSuccess: () =>
          notification.success(
            nextArchived ? 'Organization archived' : 'Organization unarchived',
          ),
        onError: (mutationError) => {
          Sentry.captureException(mutationError, {
            extra: { context: 'AdminPage.handleToggleArchive', orgId: org.id },
          });
          notification.error(
            mutationError instanceof Error
              ? mutationError.message
              : 'An unexpected error occurred',
          );
        },
      },
    );
  };

  const filteredOrganizations = useMemo(
    () => filterOrganizations(organizationList, archiveFilter, searchQuery),
    [organizationList, archiveFilter, searchQuery],
  );

  const totalPages = Math.ceil(filteredOrganizations.length / ITEMS_PER_PAGE);
  // Clamp during render instead of via a second effect: a clamp effect races
  // the reset-to-1 effect below and can win, landing on the last page after a
  // filter change shrinks the results.
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const paginatedOrganizations = useMemo(
    () =>
      filteredOrganizations.slice(
        (safePage - 1) * ITEMS_PER_PAGE,
        safePage * ITEMS_PER_PAGE,
      ),
    [filteredOrganizations, safePage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, archiveFilter]);

  if (isLoading) return <AdminSkeleton />;

  return (
    <div className='w-full'>
      <div className='flex flex-col w-full'>
        <div className='flex flex-col -mt-6 py-6 gap-6 sticky top-0 bg-background z-10'>
          <PageTitle title='Organizations' />
          <div className='flex justify-between items-start'>
            <div className='flex items-center gap-2'>
              <div className='w-96'>
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder='Search organizations by name...'
                  aria-label='Search organizations'
                />
              </div>
              <Select
                value={archiveFilter}
                onValueChange={(value) =>
                  setArchiveFilter(value as OrgArchiveFilter)
                }
              >
                <SelectTrigger
                  className='h-9 w-[130px]'
                  aria-label='Filter organizations by archive status'
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='active'>Active</SelectItem>
                  <SelectItem value='archived'>Archived</SelectItem>
                  <SelectItem value='all'>All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => {
                setCreateOrgModalOpen(true);
              }}
            >
              Create organizations
            </Button>
          </div>
        </div>

        {filteredOrganizations?.length === 0 && (
          <p className='text-gray-500'>No organizations found.</p>
        )}
        <div className='flex flex-col gap-2'>
          {paginatedOrganizations.map((org: Organization) => (
            <OrganizationItem
              key={`org-${org?.id}`}
              org={org}
              handleOpenMemberModal={handleOpenMemberModal}
              handleToggleArchive={handleToggleArchive}
              isArchiving={isArchiving}
            />
          ))}
        </div>

        {filteredOrganizations.length > 0 && totalPages > 1 && (
          <div className='mt-4 flex items-center justify-between'>
            <div className='text-sm text-muted-foreground'>
              Showing {(safePage - 1) * ITEMS_PER_PAGE + 1}-
              {Math.min(
                safePage * ITEMS_PER_PAGE,
                filteredOrganizations.length,
              )}{' '}
              of {filteredOrganizations.length}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      <CreateOrganizationModal
        isOpen={isCreateOrgModalOpen}
        updateOrganizationList={refetchOrganizations}
        setCreateOrgModalOpen={setCreateOrgModalOpen}
      />
      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        orgId={orgId}
        setOrgId={setOrgId}
        setAddUserModalOpen={setAddMemberModalOpen}
        canChooseRole
      />
    </div>
  );
};
